import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { API_BASE } from "@/lib/api";
import { fmtDate } from "@/lib/dates";
import { cn } from "@/lib/utils";
import { serverMessage } from "./ReferCopy";

const STAGE_STYLE = {
  sent: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
  offered: "bg-sky-500/15 text-sky-700 dark:text-sky-400",
  successful: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
  closed: "bg-muted text-muted-foreground",
};

const MONTH_STYLE = {
  qualified: "border-emerald-500/50 bg-emerald-500/10 text-emerald-800 dark:text-emerald-300",
  not_qualified: "border-destructive/40 bg-destructive/5 text-destructive",
  pending: "border-border bg-muted/40 text-muted-foreground",
  skipped: "border-border bg-muted/40 text-muted-foreground",
};

// Why a month did not count, in the reader's own language — the server sends the code and the
// date, never the sentence.
const monthReason = (m, t) => {
  if (m.state === "qualified" || !m.reason_code) return "";
  const line = t.monthReason[m.reason_code] || m.reason || "";
  return line.replace("{date}", fmtDate(m.checks_on));
};

/** Track referrals — the referrer's own list: where each name has reached, and for anybody who
 *  joined, whether each month counted towards a reward and why it did not when it did not. No
 *  amounts here; HR keeps the money side. */
export const ReferTrack = ({ t, me, reloadKey }) => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.post(`${API_BASE}/public/refer/track`, {
        emp_id: me.emp_id, phone: me.phone,
      });
      setRows(data.rows || []);
    } catch (e) {
      setError(serverMessage(e?.response?.data?.detail, t));
      setRows([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me.emp_id, me.phone]);

  useEffect(() => { load(); }, [load, reloadKey]);

  if (!rows && !error) {
    return (
      <p className="flex items-center gap-2 py-8 text-[13px] text-muted-foreground"
        data-testid="refer-track-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> {t.searching}
      </p>
    );
  }

  return (
    <section data-testid="refer-track">
      <p className="text-[13px] text-muted-foreground">{t.trackLead}</p>
      {error && (
        <p className="mt-3 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-[13px] text-destructive"
          data-testid="refer-track-error">{error}</p>
      )}
      {rows?.length === 0 && !error && (
        <p className="mt-6 rounded-2xl border border-dashed bg-card p-6 text-center text-[13px] text-muted-foreground"
          data-testid="refer-track-empty">{t.trackEmpty}</p>
      )}

      <div className="mt-3 space-y-3">
        {(rows || []).map((r) => (
          <article key={r.id} className="rounded-2xl border bg-card p-4"
            data-testid={`refer-track-${r.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[15px] font-semibold">{r.name}</p>
                <p className="text-[12px] text-muted-foreground">
                  {r.phone} · {r.role_name}
                </p>
              </div>
              <span className={cn("shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide",
                STAGE_STYLE[r.stage])} data-testid={`refer-track-stage-${r.id}`}>
                {t.stages[r.stage]}
              </span>
            </div>

            <p className="mt-1.5 text-[12px] text-muted-foreground">
              {t.referredOn} {fmtDate(r.referred_on)} · {t.stageHelp[r.stage]}
              {r.stage === "closed" && r.closed_reason ? ` · ${r.closed_reason}` : ""}
            </p>

            {r.months.length > 0 && (
              <div className="mt-3 border-t pt-3">
                <p className="text-[12px] font-semibold">
                  {t.monthsTitle}
                  <span className="ml-1.5 font-normal text-muted-foreground">
                    · {t.monthsCounted.replace("{n}", r.months_counted)}
                  </span>
                </p>
                <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
                  {t.monthsLead}
                </p>
                <div className="mt-2 grid grid-cols-2 gap-1.5 sm:grid-cols-3">
                  {r.months.map((m) => (
                    <div key={m.month}
                      title={monthReason(m, t)}
                      className={cn("rounded-lg border px-2 py-1.5", MONTH_STYLE[m.state])}
                      data-testid={`refer-month-${r.id}-${m.month}`}>
                      <p className="text-[11px] font-semibold">{m.label}</p>
                      <p className="text-[10px] leading-tight opacity-80">
                        {t.monthStates[m.state]}
                        {monthReason(m, t) ? ` · ${monthReason(m, t)}` : ""}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </article>
        ))}
      </div>
    </section>
  );
};
